<template>
    <div class="track-wave-root">
        <div
            ref="root"
            class="track-wave"
            :class="{ 'is-readonly': readonly }"
            @pointermove.prevent="onPointerMove"
            @pointerleave="onPointerLeave"
            @pointerdown.prevent="onPointerDown"
        >
            <canvas ref="canvas" class="wave-canvas" />
        </div>

        <div
            v-if="rangesState.length"
            class="ranges-box"
            :style="{ height: rangesState.length * RANGE_HEIGHT + 'px' }"
        >
            <div
                class="range"
                v-for="(item, i) in rangesState"
                :key="item.code"
                :class="{ disabled: !item.editable }"
                :style="{
                    top: i * RANGE_HEIGHT + 'px'
                }"
            >
                <div
                    class="range-body"
                    :style="rangeStyle(item)"
                    @pointerdown.stop="startDrag($event, 'move', i)"
                >
                    <label v-if="item.name"
                        class="title"
                        :title="item.name"
                    >
                        {{ item.name }}
                    </label>
                    <span
                        class="handle left"
                        @pointerdown.stop="startDrag($event, 'left', i)"
                    />
                    <span
                        class="handle right"
                        @pointerdown.stop="startDrag($event, 'right', i)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import stores from '@/stores';
import { formatTime } from '🔗/utils/utils';

const player = stores.player();
const locale = stores.locale();




const emit = defineEmits(['change', 'range']);

const props = defineProps({
    wave: { type: Array, default: () => ([]) },
    id: { type: String, default: '' },
    pane: { type: String, default: 'default' },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 400 },
    duration: { type: Number, required: true },
    readonly: { type: Boolean, default: false },
    simplify: { type: Number, default: 1 }, // коэффициент упрощения

    showTimeline: { type: Boolean, default: false },
    showGrid: { type: Boolean, default: false },
    timelineStep: { type: [Number, null], default: null }, // ms
    gridStep: { type: [Number, null], default: null }, // ms

    // Новое, надо добавить
    ranges: { type: [Array, null], default: null } // [{ limit: ms, value: { from, to }, editable: true }]
});

const pos = computed(() => {
    if (!player.panes[props.pane] || player.panes[props.pane]._item?.id !== props.id) {
        return 0;
    }

    const pane = player.panes[props.pane];

    return pane._playedSeconds * 1000;
});

const canvas = ref(null);
const root = ref(null);

let ctx = null;
let dpr = 1;
let resizeObserver = null;
let rafId = null;

const smoothedPos = ref(pos.value);

const hoverActive = ref(false);
const hoverIndex = ref(-1);
const dragging = ref(false);

// вычисляем "упрощённый" массив wave
const simplifiedWave = computed(() => {
    const factor = Math.max(1, Math.floor(props.simplify));
    const result = [];
    for (let i = 0; i < props.wave.length; i += factor) {
        const slice = props.wave.slice(i, i + factor);
        // можно брать максимум, чтобы пики сохранялись
        const val = Math.max(...slice);
        result.push(val);
    }
    return result;
});

// количество столбиков
const nBars = computed(() => simplifiedWave.value.length);

const getCssNumber = (varName, fallback = '0') => {
    if (!root.value) {
        return parseFloat(fallback) || 0;
    }

    const val = getComputedStyle(root.value).getPropertyValue(varName).trim();
    return val ? parseFloat(val) : parseFloat(fallback) || 0;
}

const resizeCanvas = () => {
    if (!canvas.value) {
        return;
    }

    const el = canvas.value;
    const container = root.value || el.parentElement || el;
    const rect = container.getBoundingClientRect();

    dpr = window.devicePixelRatio || 1;

    // Размеры в CSS-пикселях (от контейнера)
    const displayWidth = Math.max(1, Math.floor(rect.width));
    const displayHeight = Math.max(1, Math.floor(rect.height));

    // ВАЖНО: не трогаем style.width/height (оставляем 100% из CSS)
    // Меняем только атрибуты для бэкбуфера под DPR
    const bufferWidth = Math.max(1, Math.floor(displayWidth * dpr));
    const bufferHeight = Math.max(1, Math.floor(displayHeight * dpr));

    if (el.width !== bufferWidth || el.height !== bufferHeight) {
        el.width = bufferWidth;
        el.height = bufferHeight;
    }

    ctx = el.getContext('2d');
    // Единицы — в CSS-пикселях
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    draw();
}

const clearCanvas = () => {
    if (!ctx || !root.value) {
        return;
    }

    const rect = root.value.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
}

const drawRoundedRect = (x, y, w, h, r) => {
    if (r <= 0) {
        ctx.fillRect(x, y, w, h);
        return;
    }

    const right = x + w;
    const bottom = y + h;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(right, y, right, bottom, r);
    ctx.arcTo(right, bottom, x, bottom, r);
    ctx.arcTo(x, bottom, x, y, r);
    ctx.arcTo(x, y, right, y, r);
    ctx.closePath();
    ctx.fill();
}

const getAutoStep = (W, targetPx = 80) => {
    if (!props.duration || props.duration <= 0) {
        return 0;
    }

    const approxCount = Math.max(1, Math.floor(W / targetPx));
    const rawStep = props.duration / approxCount;

    // нормализуем к "красивым" шагам: 1,2,5 * 10^n
    const pow = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const norm = rawStep / pow;

    let base;
    if (norm <= 1) {
        base = 1;
    }
    else if (norm <= 2) {
        base = 2;
    }
    else if (norm <= 5) {
        base = 5;
    }
    else {
        base = 10;
    }

    return base * pow;
}

const drawGrid = (W, H, reservedBottom = 0) => {
    if (!props.showGrid || !props.duration) {
        return;
    }

    const css = getComputedStyle(root.value);
    const strokeColor = css.getPropertyValue('--wave-grid-color') || 'rgba(0,0,0,0.08)';

    const step = props.gridStep > 0 ? props.gridStep : getAutoStep(W, 40);
    if (!step) {
        return;
    }

    ctx.save();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 1;

    const pxPerMs = W / props.duration;
    const bottomLimit = H - reservedBottom;

    for (let t = 0; t <= props.duration; t += step) {
        let x = t * pxPerMs;
        if (locale.rtl) x = W - x;

        ctx.beginPath();
        ctx.moveTo(x + 0.5, 4);
        ctx.lineTo(x + 0.5, bottomLimit - 4);
        ctx.stroke();
    }

    ctx.restore();
}

const drawTimeline = (W, H, bottomPadding = 8) => {
    if (!props.showTimeline || !props.duration) {
        return;
    }

    const css = getComputedStyle(root.value);
    const fillStyle = css.getPropertyValue('--wave-timeline-color') || 'rgba(0,0,0,0.6)';

    const step = props.timelineStep > 0 ? props.timelineStep : getAutoStep(W, 40);
    if (!step) {
        return;
    }

    ctx.save();
    ctx.fillStyle = fillStyle;
    ctx.font = '10px sans-serif';
    ctx.textBaseline = 'bottom';

    const metrics = ctx.measureText('00:00');
    const textHeight = (metrics.actualBoundingBoxAscent || 8) + (metrics.actualBoundingBoxDescent || 2);
    const pxPerMs = W / props.duration;

    for (let t = 0; t <= props.duration; t += step) {
        let x = t * pxPerMs;

        if (locale.rtl) {
            x = W - x;
        }

        const label = formatTime(t);
        const textWidth = ctx.measureText(label).width;

        // защита от выхода за границы
        if (x - textWidth / 2 < 0) {
            ctx.textAlign = 'left';
            x = 0;
        } else if (x + textWidth / 2 > W) {
            ctx.textAlign = 'right';
            x = W;
        } else {
            ctx.textAlign = 'center';
        }

        ctx.fillText(label, x, H);
    }

    ctx.restore();
}

const draw = () => {
    if (!ctx || !root.value) {
        return;
    }

    const rect = root.value.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;

    clearCanvas();

    // сначала вычисляем сколько места нужно под таймлайн
    let reservedBottom = 0;

    if (props.showTimeline) {
        ctx.save();
        ctx.font = '10px sans-serif';
        const metrics = ctx.measureText('00:00');
        const textHeight = (metrics.actualBoundingBoxAscent || 8) + (metrics.actualBoundingBoxDescent || 2);
        reservedBottom = textHeight + 8; // гарантированный отступ 8px
        ctx.restore();
    }

    drawGrid(W, H, reservedBottom);

    const n = nBars.value ?? (props.wave?.length || 0);
    if (n === 0) {
        return;
    }

    const css = getComputedStyle(root.value);
    const unplayedColor = css.getPropertyValue('--wave-unplayed') || '#e6e6e6';
    const playedColor = css.getPropertyValue('--wave-played') || '#ff5500';
    const hoverColor = css.getPropertyValue('--wave-hover') || '#ff8a4d';
    const centerBg = css.getPropertyValue('--wave-bg') || 'transparent';
    const gapToken = css.getPropertyValue('--wave-gap').trim();
    const gap = gapToken ? parseFloat(gapToken) : 2;
    const radiusToken = css.getPropertyValue('--wave-radius').trim();
    const barRadius = radiusToken ? parseFloat(radiusToken) : 2;

    if (centerBg && centerBg !== 'transparent') {
        ctx.fillStyle = centerBg;
        ctx.fillRect(0, 0, W, H);
    }

    const usableHeight = H - reservedBottom;

    const totalGap = gap * (n - 1);
    let barW = (W - totalGap) / n;
    if (barW < 1) barW = Math.max(0.5, W / (n * 2));

    const vMin = props.min;
    const vMax = Math.max(props.max, vMin + 1);
    const valueRange = vMax - vMin;

    const centerY = usableHeight / 2;
    const playedThreshold = Math.max(
        0,
        Math.min(smoothedPos.value / Math.max(1, props.duration), 1)
    ) * n;

    for (let i = 0; i < n; i++) {
        const raw = Number(simplifiedWave.value ? simplifiedWave.value[i] : props.wave[i]) || 0;
        let frac = (raw - vMin) / valueRange;
        frac = Math.max(0, Math.min(1, frac));
        const barHeight = Math.max(1, frac * usableHeight);

        const drawIndex = locale.rtl ? (n - 1 - i) : i;
        const x = drawIndex * (barW + gap);
        const y = centerY - barHeight / 2;

        let state = 'unplayed';
        if (hoverActive.value && !props.readonly && hoverIndex.value >= 0) {
            if (i <= hoverIndex.value) state = 'hover';
            else if (i < playedThreshold) state = 'played';
        } else {
            if (i < playedThreshold) state = 'played';
        }

        if (state === 'played') ctx.fillStyle = playedColor.trim();
        else if (state === 'hover') ctx.fillStyle = hoverColor.trim();
        else ctx.fillStyle = unplayedColor.trim();

        drawRoundedRect(x, y, barW, barHeight, barRadius);
    }

    drawTimeline(W, H, 8);
}

const clientXToIndex = (clientX) => {
    const container = root.value || canvas.value;
    const rect = container.getBoundingClientRect();
    let x = clientX - rect.left;
    const W = rect.width;

    if (locale.rtl) {
        x = W - x;
    }

    const n = nBars.value ?? (props.wave?.length || 0);
    if (n === 0) {
        return -1;
    }

    const gap = getCssNumber('--wave-gap', '2');
    const totalGap = gap * (n - 1);
    let barW = (W - totalGap) / n;

    if (barW < 1) {
        barW = Math.max(0.5, W / (n * 2));
    }

    let idx = Math.floor(x / (barW + gap));
    if (idx < 0) {
        idx = 0;
    }

    if (idx >= n) {
        idx = n - 1;
    }

    return idx;
}

const indexToMs = (index) => {
    const n = nBars.value;
    if (!props.duration || n <= 0) {
        return 0;
    }

    const frac = Math.max(0, Math.min(1, (index + 0.5) / n));
    return frac * props.duration;
}

const EDGE_TOLERANCE_PX = 24; // 20–32px нормально для mobile

// const clientXToMs = (clientX) => {
//     const container = root.value || canvas.value;
//     const rect = container.getBoundingClientRect();
//     let x = clientX - rect.left;

//     const W = rect.width;

//     if (locale.rtl) {
//         x = W - x;
//     }

//     const frac = Math.max(0, Math.min(1, x / W));
//     return frac * props.duration;
// };

const clientXToMs = (clientX) => {
    const container = root.value || canvas.value;
    const rect = container.getBoundingClientRect();
    let x = clientX - rect.left;
    const W = rect.width;

    if (locale.rtl) {
        x = W - x;
    }

    if (x <= EDGE_TOLERANCE_PX) {
        return 0;
    }

    if (x >= W - EDGE_TOLERANCE_PX) {
        return props.duration;
    }

    const frac = x / W;
    return frac * props.duration;
};


const onPointerMove = (e) => {
    if (!canvas.value) {
        return;
    }

    if (props.readonly) {
        return;
    }

    const idx = clientXToIndex(e.clientX);
    if (idx !== hoverIndex.value) {
        hoverIndex.value = idx;
        hoverActive.value = true;
        draw();
    }
    if (dragging.value) {
        const ms = clientXToMs(e.clientX);
        // const ms = indexToMs(idx);

        onChange(Math.round(ms));
    }
}

const onPointerLeave = () => {
    if (dragging.value) {
        return;
    }

    hoverActive.value = false;
    hoverIndex.value = -1;
    draw();
}

let changeTimeout, rangeTimeout;

const kickPlayer = async (position) => {
    clearTimeout(changeTimeout);
    changeTimeout = setTimeout(async () => {
        if (!player.panes[props.pane] || player.panes[props.pane]._item?.id !== props.id) {
            return;
        }

        const state = player.panes[props.pane]._state;

        if (state !== 'playing' && player.panes[props.pane]) {
            if (state === 'paused') {
                await player.panes[props.pane].resume();
            }
            if (state === 'stopped') {
                await player.panes[props.pane].play();
            }
        }

        player.panes[props.pane].seek(position || 0);
    }, 100)
}

const onChange = async (position) => {
    emit('change', position);

    kickPlayer(position);
}

const onPointerDown = (e) => {
    if (props.readonly) {
        return;
    }

    if (!canvas.value) {
        return;
    }

    const idx = clientXToIndex(e.clientX);
    if (idx < 0) {
        return;
    }

    const el = e.currentTarget || root.value;

    try {
        el.setPointerCapture && el.setPointerCapture(e.pointerId);
    } catch (err) {}

    dragging.value = true;
    hoverActive.value = true;
    hoverIndex.value = idx;
    draw();
    // const ms = indexToMs(idx);
    const ms = clientXToMs(e.clientX);
    onChange(Math.round(ms));

    const onUp = (ev) => {
        try {
            el.releasePointerCapture && el.releasePointerCapture(ev.pointerId);
        } catch (err) {}
        dragging.value = false;
        window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointerup', onUp);
}

const startAnimationLoop = () => {
    const ease = 0.12;

    function loop() {
        const target = pos.value || 0;
        smoothedPos.value += (target - smoothedPos.value) * ease;

        if (smoothedPos.value < 1 || !pos.value) {
            smoothedPos.value = 0;
        }

        draw();
        rafId = requestAnimationFrame(loop);
    }

    if (!rafId) {
        rafId = requestAnimationFrame(loop);
    }
}

const stopAnimationLoop = () => {
    if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
    }
}

// RANGE
    const msToPx = (ms, width) => {
        if (!props.duration) return 0;
        return (ms / props.duration) * width;
    }

    const pxToMs = (px, width) => {
        if (!props.duration) return 0;
        return (px / width) * props.duration;
    }

    const RANGE_HEIGHT = 28;

    const rangesState = ref([]);

    watch(
        () => props.ranges,
        (val) => {
            nextTick(() => {
                if (!val) {
                    rangesState.value = [];
                    return;
                }

                rangesState.value = val.map(r => ({
                    ...r,
                    from: r.value?.from ?? 0,
                    to: r.value?.to ?? r.limitMin ?? 0
                }));
            });
        },
        { immediate: true, deep: true }
    );

    const activeDrag = ref(null);
    // { type: 'left' | 'right' | 'move', index, startX, startFrom, startTo }

    const startDrag = (e, type, index) => {
        if (!rangesState.value[index].editable) return;

        const rect = root.value.getBoundingClientRect();

        activeDrag.value = {
            type,
            index,
            startX: e.clientX,
            rectWidth: rect.width,
            startFrom: rangesState.value[index].from,
            startTo: rangesState.value[index].to
        };

        window.addEventListener('pointermove', onDrag);
        window.addEventListener('pointerup', stopDrag);
    };

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

    const onDrag = (e) => {
        if (!activeDrag.value) return;

        const d = activeDrag.value;
        const range = rangesState.value[d.index];

        const dx = e.clientX - d.startX;
        const deltaMs = pxToMs(dx, d.rectWidth);

        const duration = props.duration;
        const limitMax = range.limitMax ?? duration;
        const limitMin = range.limitMin ?? 0;

        let newFrom = d.startFrom;
        let newTo = d.startTo;

        if (d.type === 'left') {
            newFrom = d.startFrom + deltaMs;

            // нельзя правее правого ушка - limitMin
            newFrom = Math.min(newFrom, d.startTo - limitMin);

            // нельзя левее 0
            newFrom = Math.max(0, newFrom);

            // превышение limitMax
            if (d.startTo - newFrom > limitMax) {
                newTo = newFrom + limitMax;
            }
        }

        if (d.type === 'right') {
            newTo = d.startTo + deltaMs;

            // нельзя левее left + limitMin
            newTo = Math.max(newTo, d.startFrom + limitMin);

            // нельзя правее duration
            newTo = Math.min(duration, newTo);

            // превышение limitMax
            if (newTo - d.startFrom > limitMax) {
                newFrom = newTo - limitMax;
            }
        }

        if (d.type === 'move') {
            const width = d.startTo - d.startFrom;

            newFrom = d.startFrom + deltaMs;
            newTo = d.startTo + deltaMs;

            if (newFrom < 0) {
                newFrom = 0;
                newTo = width;
            }

            if (newTo > duration) {
                newTo = duration;
                newFrom = duration - width;
            }
        }

        range.from = clamp(newFrom, 0, duration);
        range.to = clamp(newTo, 0, duration);

        range.changed = true;

        clearTimeout(rangeTimeout);
        rangeTimeout = setTimeout(async () => {
            emit('range', {
                from: Math.round(range.from),
                to: Math.round(range.to),
                code: range.code
            });
           
            onChange(Math.round(range.from))
        }, 100)
    };

    const stopDrag = () => {
        const index = activeDrag.value.index;
        const range = rangesState.value[index];
        activeDrag.value = null;
        window.removeEventListener('pointermove', onDrag);
        window.removeEventListener('pointerup', stopDrag);

        if (!range.changed) {
            kickPlayer(range.from);
        }
    };

    const rangeStyle = (r) => {
        if (!root.value) return {};

        const rect = root.value.getBoundingClientRect();
        const width = rect.width;

        const left = msToPx(r.from, width);
        const w = msToPx(r.to - r.from, width);

        return {
            left: left + 'px',
            width: w + 'px'
        };
    };



onMounted(() => {
    nextTick(() => {
        resizeCanvas();

        if (window.ResizeObserver && root.value) {
            resizeObserver = new ResizeObserver(resizeCanvas);
            resizeObserver.observe(root.value);
        } else {
            window.addEventListener('resize', resizeCanvas);
        }

        smoothedPos.value = pos.value || 0;
        startAnimationLoop();
    });
});

onBeforeUnmount(() => {
    if (resizeObserver && root.value) {
        resizeObserver.unobserve(root.value);
    }
    else {
        window.removeEventListener('resize', resizeCanvas);
    }

    stopAnimationLoop();
});

watch(
    () => [
        props.wave, props.min, props.max, props.duration, props.simplify, 
        props.showGrid, props.showTimeline, props.timelineStep, props.gridStep
    ],
    () => {
        nextTick(resizeCanvas);
    },
    { deep: true }
);

watch(
    () => pos.value,
    () => {
        if (!rafId) {
            startAnimationLoop();
        }
    }
);
</script>

<style lang="scss" scoped>
.track-wave {
    width: 100%;
    height: 100%;
    position: relative;
    touch-action: none;
    display: block;
    --wave-unplayed: var(--gray-20);
    --wave-played: var(--primary-500);
    --wave-hover: var(--primary-20);
    --wave-bg: transparent;
    --wave-gap: 2;
    --wave-radius: 2;
    --wave-grid-color: var(--gray-10);
    --wave-timeline-color: var(--gray-80);

    .wave-canvas {
        width: 100%;
        height: 100%;
        display: block;
        user-select: none;
        -webkit-user-drag: none;
        background: transparent;
    }

    &.is-readonly {
        cursor: default;
    }
    &:not(.is-readonly) {
        cursor: pointer;
    }

    &.invert {
        --wave-unplayed: var(--dark-20);
    }
}

.track-wave:focus {
    outline: none;
}


.track-wave-root {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
}

.ranges-box {
    // position: absolute;
    left: 0;
    right: 0;
    top: 0;
    pointer-events: none;

    .range {
        position: relative;
        left: 0;
        right: 0;
        height: 28px;
        .range-body {
            position: absolute;
            height: 100%;
            background: var(--primary-10);
            border: 1px solid var(--primary-500);
            border-radius: 4px;
            pointer-events: auto;
            cursor: grab;
            border-radius: 8px;

            .title {
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translateX(-50%) translateY(-50%);
                white-space: nowrap;
                max-width: 100%;
                font: var(--font-caption);
                color: var(--dark-80);
                overflow: hidden;
                text-overflow: ellipsis;
                pointer-events: none;
                padding: 0 8px;
            }

            &:active {
                cursor: grabbing;
            }
        }

        &.disabled {
            .range-body {
                background: var(--gray-10);
                border-color: var(--gray-500);
            }
            .handle {
                background: var(--gray-500);
            }
        }

        .handle {
            position: absolute;
            top: -1px;
            bottom: -1px;
            width: 10px;
            // height: 100%;
            background: var(--primary-500);
            cursor: ew-resize;

            // &:after {
            //     content: '';
            //     background: var(--primary-500);
            //     width: 1px;
            //     bottom: 8px;
            //     top: -100px;
            //     position: absolute;
            // }

            &.left {
                left: -5px;
                border-top-left-radius: 8px;
                border-bottom-left-radius: 8px;
                // &:after {
                //     left: -2px;
                // }
            }

            &.right {
                border-top-right-radius: 8px;
                border-bottom-right-radius: 8px;
                right: -5px;
                // &:after {
                //     right: -2px;
                // }
            }
        }
    }
}

</style>
